import React, { useState } from 'react';
import Voting from './components/Voting';

// Mock SF2 contest mirroring real Firestore shape
const mockContest = {
  id: 'euro2026sf2',
  name: 'Eurovision 2026 Semi-Final 2',
  closed: false,
  contestants: [
    { id: 1, country: 'Bulgaria', name: 'Dara - Bangaranga' },
    { id: 2, country: 'Azerbaijan', name: 'Jiva - Just Go' },
    { id: 3, country: 'Romania', name: 'Alexandra Căpitănescu - Choke Me' },
    { id: 4, country: 'Luxembourg', name: 'Eva Marija - Mother Nature' },
    { id: 5, country: 'Czechia', name: 'Daniel Žižka - Crossroads' },
    { id: 6, country: 'France', name: 'Monroe - Regarde !', nonCompeting: true },
    { id: 7, country: 'Armenia', name: 'Simón - Paloma Rumba' },
    { id: 8, country: 'Switzerland', name: 'Veronica Fusaro - Alice' },
    { id: 9, country: 'Cyprus', name: 'Antigoni - Jalla' },
    { id: 10, country: 'Austria', name: 'Cosmó - Tanzschein', nonCompeting: true },
    { id: 11, country: 'Latvia', name: 'Atvara - Ēnā' },
    { id: 12, country: 'Denmark', name: 'Søren Torpegaard Lund - Før vi går hjem' },
    { id: 13, country: 'Australia', name: 'Delta Goodrem - Eclipse' },
    { id: 14, country: 'Ukraine', name: 'Leléka - Ridnym' },
    { id: 15, country: 'United Kingdom', name: 'Look Mum No Computer - Eins, Zwei, Drei', nonCompeting: true },
    { id: 16, country: 'Albania', name: 'Alis - Nân' },
    { id: 17, country: 'Malta', name: 'Aidan - Bella' },
    { id: 18, country: 'Norway', name: 'Jonas Lovv - Ya ya ya' },
  ],
  runningOrder: Array.from({ length: 18 }, (_, i) => ({ contestantId: String(i + 1), order: i + 1, durationSeconds: 180 })),
};

const mockUser = { id: 'qa-mock', name: 'QA', isAdmin: false };
const mockUserAdmin = { id: 'qa-mock-admin', name: 'QAA', isAdmin: true };

// Mock allScores: contestant 1 has 2 voters, contestant 2 has 1 voter
const mockScores = {
  '1': {
    'voter-a': { songQuality: 8, staging: 9, vocalQuality: 7, overall: 7.9, voterName: 'Voter A', contestId: 'euro2026sf2' },
    'voter-b': { songQuality: 6, staging: 5, vocalQuality: 7, overall: 6.1, voterName: 'Voter B', contestId: 'euro2026sf2' },
  },
  '2': {
    'voter-a': { songQuality: 9, staging: 9, vocalQuality: 9, overall: 9, voterName: 'Voter A', contestId: 'euro2026sf2' },
  },
};

// Mock reactions on contestant 1 — many varied emojis to test stack
const mockReactions = Array.from({ length: 35 }).map((_, i) => ({
  id: 'r' + i,
  emoji: ['🔥', '😍', '🤣', '👑', '🌟', '🤯', '👏', '💃', '🏳️‍🌈', '💀', '🗿'][i % 11],
  contestantId: i % 3 === 0 ? '1' : (i % 3 === 1 ? '6' : null),
  timestamp: { toMillis: () => Date.now() - (i * 1500), toDate: () => new Date(Date.now() - (i * 1500)) },
}));

const QAHarness = () => {
  const [activeOrder, setActiveOrder] = useState(3); // simulating act 3 currently playing
  const [user, setUser] = useState(mockUser);
  const [lastChanged, setLastChanged] = useState(null);
  const [scores, setScores] = useState(mockScores);

  const updateScore = (contestantId, criterionId, value) => {
    setScores(prev => {
      const next = { ...prev };
      const key = String(contestantId);
      const existing = { ...(next[key]?.[user.id] || {}) };
      existing[criterionId] = value;
      existing.contestId = 'euro2026sf2';
      // recompute overall
      const w = { songQuality: 0.4, staging: 0.25, vocalQuality: 0.35 };
      let total = 0, hasAll = true;
      ['songQuality', 'staging', 'vocalQuality'].forEach(c => {
        if (existing[c] !== undefined) total += existing[c] * w[c];
        else hasAll = false;
      });
      if (hasAll) existing.overall = total;
      next[key] = { ...(next[key] || {}), [user.id]: existing };
      return next;
    });
  };

  const triggerFlash = (contestantId, overall) => {
    setLastChanged({ contestantId: String(contestantId), voterId: 'someone-else', overall, timestamp: Date.now() });
  };

  return (
    <div style={{ background: 'var(--bg)', color: 'var(--text)', minHeight: '100vh', padding: 16 }}>
      <div style={{ position: 'sticky', top: 0, background: 'var(--bg)', zIndex: 50, padding: 8, borderBottom: '2px solid var(--pink)', marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontFamily: 'JetBrains Mono, monospace', marginBottom: 6 }}>QA HARNESS — visual verify. activeOrder = {activeOrder}, role = {user.isAdmin ? 'ADMIN' : 'USER'}</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => setActiveOrder(null)}>activeOrder=null (pre-show)</button>
          <button onClick={() => setActiveOrder(0)}>activeOrder=0</button>
          <button onClick={() => setActiveOrder(3)}>activeOrder=3 (3 unlocked)</button>
          <button onClick={() => setActiveOrder(10)}>activeOrder=10</button>
          <button onClick={() => setActiveOrder(18)}>activeOrder=18 (all unlocked)</button>
          <button onClick={() => setUser(user.isAdmin ? mockUser : mockUserAdmin)}>Toggle admin (now: {user.isAdmin ? 'admin' : 'user'})</button>
          <button onClick={() => triggerFlash(1, 9)}>Flash HIGH on #1</button>
          <button onClick={() => triggerFlash(2, 6)}>Flash MID on #2</button>
          <button onClick={() => triggerFlash(3, 3)}>Flash LOW on #3</button>
        </div>
      </div>
      <Voting
        activeContest={mockContest}
        updateScore={updateScore}
        allScores={scores}
        user={user}
        reactions={mockReactions}
        lastChanged={lastChanged}
        activeContestantId={activeOrder ? String(activeOrder) : null}
        activeOrder={activeOrder}
      />
    </div>
  );
};

export default QAHarness;
